/* ==========================================================================
   ui.js — interactive chrome: mobile menu, accordion, review
   carousel, form validation, pillar tilt.
   Every widget here is keyboard operable and announces state.
   ========================================================================== */

(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var touch = window.matchMedia('(hover: none)').matches;

  /* --- Mobile menu -------------------------------------------------------- */

  var toggle = document.querySelector('[data-menu-toggle]');
  var menu = document.querySelector('[data-menu]');

  if (toggle && menu) {
    var links = menu.querySelectorAll('.menu__list a');
    Array.prototype.forEach.call(links, function (a, i) { a.style.setProperty('--i', i); });

    var setMenu = function (open) {
      toggle.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('is-locked', open);
      if (open) {
        menu.hidden = false;
        requestAnimationFrame(function () { menu.classList.add('is-open'); });
        links[0] && links[0].focus();
      } else {
        menu.classList.remove('is-open');
        setTimeout(function () { menu.hidden = true; }, reduce ? 0 : 500);
        toggle.focus();
      }
    };

    toggle.addEventListener('click', function () {
      setMenu(toggle.getAttribute('aria-expanded') !== 'true');
    });

    menu.addEventListener('click', function (e) {
      if (e.target.closest('a')) setMenu(false);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') setMenu(false);
    });
  }

  /* --- Accordion ---------------------------------------------------------- */

  document.querySelectorAll('[data-accordion]').forEach(function (root) {
    var items = root.querySelectorAll('.accordion__item');

    items.forEach(function (item, i) {
      item.style.setProperty('--i', i);
      var trigger = item.querySelector('.accordion__trigger');
      var panel = item.querySelector('.accordion__panel');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', function () {
        var open = trigger.getAttribute('aria-expanded') === 'true';

        // One open at a time.
        items.forEach(function (other) {
          var t = other.querySelector('.accordion__trigger');
          var p = other.querySelector('.accordion__panel');
          if (!t || !p) return;
          t.setAttribute('aria-expanded', 'false');
          p.classList.remove('is-open');
          p.style.height = '0px';
          other.classList.remove('is-open');
        });

        if (!open) {
          trigger.setAttribute('aria-expanded', 'true');
          panel.classList.add('is-open');
          panel.style.height = panel.firstElementChild.offsetHeight + 'px';
          item.classList.add('is-open');
        }
      });

      trigger.addEventListener('keydown', function (e) {
        var order = Array.prototype.slice.call(root.querySelectorAll('.accordion__trigger'));
        var idx = order.indexOf(trigger);
        if (e.key === 'ArrowDown') { e.preventDefault(); order[(idx + 1) % order.length].focus(); }
        if (e.key === 'ArrowUp')   { e.preventDefault(); order[(idx - 1 + order.length) % order.length].focus(); }
        if (e.key === 'Home')      { e.preventDefault(); order[0].focus(); }
        if (e.key === 'End')       { e.preventDefault(); order[order.length - 1].focus(); }
      });
    });
  });

  /* --- Review carousel ---------------------------------------------------- */

  document.querySelectorAll('[data-reviews]').forEach(function (root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('.review'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('.reviews__dot'));
    var live = root.querySelector('[data-reviews-live]');
    var index = 0;
    var timer = null;

    function show(next) {
      index = (next + slides.length) % slides.length;
      slides.forEach(function (s, i) {
        s.classList.toggle('is-active', i === index);
        s.setAttribute('aria-hidden', String(i !== index));
      });
      dots.forEach(function (d, i) { d.setAttribute('aria-selected', String(i === index)); });
      if (live) live.textContent = 'Review ' + (index + 1) + ' of ' + slides.length + ', ' + slides[index].dataset.author;
    }

    function play() {
      if (reduce) return;
      stop();
      timer = setInterval(function () { show(index + 1); }, 7000);
    }
    function stop() { if (timer) clearInterval(timer); timer = null; }

    dots.forEach(function (d, i) { d.addEventListener('click', function () { show(i); play(); }); });

    root.querySelectorAll('[data-reviews-prev]').forEach(function (b) {
      b.addEventListener('click', function () { show(index - 1); play(); });
    });
    root.querySelectorAll('[data-reviews-next]').forEach(function (b) {
      b.addEventListener('click', function () { show(index + 1); play(); });
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); show(index - 1); play(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); show(index + 1); play(); }
    });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', play);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', play);
    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : play();
    });

    root.querySelectorAll('.review__more').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var body = btn.previousElementSibling;
        var clamped = body.classList.toggle('is-clamped');
        btn.textContent = clamped ? 'Read more' : 'Read less';
        btn.setAttribute('aria-expanded', String(!clamped));
      });
    });

    show(0);
    play();
  });

  /* --- Forms -------------------------------------------------------------- */

  document.querySelectorAll('[data-validate]').forEach(function (form) {
    var fields = Array.prototype.slice.call(form.querySelectorAll('input, select, textarea'));

    function message(field) {
      if (field.validity.valueMissing) return 'Please fill this in so we can get back to you.';
      if (field.validity.typeMismatch && field.type === 'email') return 'Please check the email address.';
      if (field.validity.tooShort) return 'A little more detail, please.';
      return 'Please check this field.';
    }

    function setError(field, text) {
      var slot = field.closest('.field').querySelector('.field__error');
      if (!slot) return;
      slot.textContent = text || '';
      field.setAttribute('aria-invalid', text ? 'true' : 'false');
    }

    fields.forEach(function (field) {
      field.addEventListener('input', function () { setError(field, ''); });
    });

    form.addEventListener('submit', function (e) {
      var firstBad = null;
      fields.forEach(function (field) {
        if (field.checkValidity()) { setError(field, ''); return; }
        setError(field, message(field));
        if (!firstBad) firstBad = field;
      });
      if (firstBad) {
        e.preventDefault();
        firstBad.focus();
      }
    });
  });

  /* --- Pillar tilt -------------------------------------------------------- */

  if (!touch && !reduce) {
    document.querySelectorAll('[data-tilt]').forEach(function (card) {
      var frame = null;

      card.addEventListener('pointermove', function (e) {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          var r = card.getBoundingClientRect();
          var x = (e.clientX - r.left) / r.width - 0.5;
          var y = (e.clientY - r.top) / r.height - 0.5;
          // Capped at 3 degrees. Subtle, not a novelty.
          card.style.transform = 'perspective(900px) rotateY(' + (x * 3).toFixed(2) + 'deg) rotateX(' + (-y * 3).toFixed(2) + 'deg)';
          frame = null;
        });
      });

      card.addEventListener('pointerleave', function () {
        card.style.transform = '';
      });
    });
  }

  if (touch) {
    document.querySelectorAll('[data-tilt]').forEach(function (card) {
      card.addEventListener('touchstart', function () { card.classList.add('is-filled'); }, { passive: true });
    });
  }
})();
